import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {RowData} from 'notebook/tables/TableConstants';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

import {StructuredTableColumn} from '../StructuredTableConstants';

/**
 * The main export from this file (getValuesFromSelection) gives you a way to extract IDs (etc)
 * from a table from which the user has selected cells.
 *
 * Three functions are required. It sounds like a lot, but they're all necessary to make this tool
 * fully parameterized:
 *
 *  - a column filter (e.g., "only consider columns with CONTAINERS")
 *  - a cell filter, which is applied only when a cell is in a column which passes through the column
 *     filter (e.g., "only consider cells which are STORABLES")
 *  - a value extractor (e.g., "given this cell, here are the values we're interested in")
 */

type ColumnFilterFunction = (column: StructuredTableColumn) => boolean;
type CellFilterFunction = (cellValue: TypedValue, column: StructuredTableColumn, rowData: RowData) => boolean;
type ValueGetterFunction<T> = (cellValue: TypedValue, column: StructuredTableColumn) => Array<T>;

/**
 The type parameter <T> in this function allows you to retrieve values of any type (e.g., string or
 TypedValue, or even Cell). It is inferred from the return value of your `getCellValues` callback. If
 the callback returns an array of strings, then this function will also return a (larger) array of strings.
*/
export default function getValuesInSelection<T>(
  multiSelectionBox: MultiSelectionBox | null,
  columns: Array<StructuredTableColumn>,
  computedTable: Array<Array<EvaluatedCell>>,
  rowData: Array<RowData>,
  callbacks: {
    columnFilter: ColumnFilterFunction;
    cellFilter: CellFilterFunction;
    getCellValues: ValueGetterFunction<T>;
  }
): Array<T> {
  if (multiSelectionBox == null) {
    return [];
  }

  const {minI, maxI: inclusiveMaxI, minJ, maxJ: inclusiveMaxJ} = multiSelectionBox;
  let values: Array<T> = [];

  for (let i = minI; i <= inclusiveMaxI; i++) {
    for (let j = minJ; j <= inclusiveMaxJ; j++) {
      // multiSelectionBox may be larger than the table
      const column = columns[j];
      if (column == null || !callbacks.columnFilter(column)) {
        continue;
      }

      // Don't crash if the computed table's dimensions get out of sync with the selection state
      const computedTableRow = computedTable[i];
      if (!computedTableRow) {
        continue;
      }
      const computedTableCell = computedTableRow[j];
      if (!computedTableCell) {
        continue;
      }
      const rowDatum = rowData[i];
      if (!rowDatum) {
        continue;
      }

      const typedValue = computedTableCell.value;
      if (callbacks.cellFilter(typedValue, column, rowDatum)) {
        values = [...values, ...callbacks.getCellValues(typedValue, column)];
      }
    }
  }

  return values;
}
